import { MessagesButton } from "@/components/BookingMessages";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, imgUrl, errMsg, fmtDate, money } from "@/lib/api";
import StatusBadge, { bookingState } from "@/components/StatusBadge";
import { ReviewDialog, ComplaintDialog } from "@/components/Dialogs";
import { CancelStayDialog } from "@/components/CancelStayDialog";
import { Spinner } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Luggage } from "lucide-react";

function TripRow({ b, reload }) {
  const today = new Date().toISOString().slice(0, 10);
  const cancel = async () => {
    try {
      await api.post(`/bookings/${b.booking_id}/cancel`);
      toast.success("Request cancelled");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-col gap-5 rounded-3xl border bg-white p-4 sm:flex-row" data-testid={`trip-${b.booking_id}`}>
      <Link to={`/listings/${b.listing_id}`} className="h-40 shrink-0 overflow-hidden rounded-2xl bg-stone-100 sm:h-32 sm:w-44">
        <img src={imgUrl(b.listing.photos?.[0])} alt="" className="h-full w-full object-cover" />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={bookingState(b)} testid={`trip-status-${b.booking_id}`} />
          {b.is_test && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800" data-testid={`trip-test-badge-${b.booking_id}`}>TEST BOOKING · no real charge</span>}
          {b.refunded_amount > 0 && <span className="text-xs font-semibold text-sky-700">Refunded {money(b.refunded_amount)}</span>}
        </div>
        <Link to={`/listings/${b.listing_id}`} className="truncate text-lg font-semibold hover:underline">{b.listing.title}</Link>
        {b.listing.address && <p className="text-sm text-stone-700" data-testid={`trip-address-${b.booking_id}`}>{b.listing.address}</p>}
        <p className="text-sm text-stone-600">{fmtDate(b.check_in)} → {fmtDate(b.check_out)} · {b.nights} night{b.nights === 1 ? "" : "s"} · {b.guests} guest{b.guests === 1 ? "" : "s"} · Host {b.host?.name}</p>
        <p className="text-sm">Total <span className="font-mono-num font-semibold">{money(b.total)}</span> <span className="text-emerald-700">· no FairStay guest fee</span></p>
      </div>
      <div className="flex flex-wrap items-start gap-2 sm:flex-col sm:items-end">
        <MessagesButton bookingId={b.booking_id} />
        {b.payment_intent_id && <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`trip-receipt-${b.booking_id}`}><Link to={`/trips/${b.booking_id}/receipt`}>Receipt</Link></Button>}
        {b.status === "accepted" && <Button asChild data-testid={`stripe-pay-button-${b.booking_id}`} className="rounded-full"><Link to={`/trips/${b.booking_id}/pay`}>{`Pay ${money(b.total)}`}</Link></Button>}
        {["requested", "accepted"].includes(b.status) && <Button data-testid={`trip-cancel-${b.booking_id}`} size="sm" variant="ghost" className="rounded-full" onClick={cancel}>Cancel request</Button>}
        {b.status === "confirmed" && b.check_in > today && <CancelStayDialog booking={b} onDone={reload} />}
        {b.completed && !b.has_reviewed && <ReviewDialog booking={b} who="this place" onDone={reload} />}
        {b.status === "confirmed" && b.check_in <= today && !b.complaint && <ComplaintDialog booking={b} />}
        {b.complaint && <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`trip-complaint-link-${b.booking_id}`}><Link to={`/complaints/${b.complaint.complaint_id}`}>View complaint</Link></Button>}
      </div>
    </div>
  );
}

export default function Trips() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["trips"], queryFn: () => api.get("/bookings/mine").then((r) => r.data) });
  const reload = () => qc.invalidateQueries({ queryKey: ["trips"] });
  if (isLoading) return <Spinner />;
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="mb-8 text-4xl font-bold tracking-tight">Trips</h1>
      {data.length === 0 ? (
        <div className="rounded-3xl border bg-white py-20 text-center" data-testid="trips-empty">
          <Luggage className="mx-auto mb-3 h-9 w-9 text-stone-400" />
          <p className="mb-4 font-semibold">No trips yet</p>
          <Button asChild className="rounded-full"><Link to="/search">Find a stay</Link></Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="trips-list">{data.map((b) => <TripRow key={b.booking_id} b={b} reload={reload} />)}</div>
      )}
    </div>
  );
}
